import { cva, type VariantProps } from 'class-variance-authority'
import { ProgressBar as AriaProgressBar, type ProgressBarProps as AriaProgressBarProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link ProgressBar}.
 *
 * All sizing and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm (h-1), md (h-2, default), lg (h-3)
 * - Uses the brand primary color for fill
 *
 * @example
 * ```tsx
 * const classes = progressBarVariants({ size: 'md' })
 * // Returns: "h-2"
 * ```
 */
export const progressBarVariants = cva('', {
	variants: {
		size: {
			sm: 'h-1',
			md: 'h-2',
			lg: 'h-3',
		},
	},
	defaultVariants: { size: 'md' },
})

/**
 * Props for the {@link ProgressBar} component.
 *
 * Extends React Aria's ProgressBarProps with CVA size variants.
 *
 * @property size - Progress bar size: 'sm' (h-1) | 'md' (h-2, default) | 'lg' (h-3)
 * @property className - Optional additional Tailwind classes
 * @property value - Current progress value (0-100)
 * @property label - Accessible label
 * @property isIndeterminate - Indeterminate state (no specific progress)
 *
 * @example
 * ```tsx
 * <ProgressBar value={60} label="Upload progress" size="md" />
 * ```
 */
export interface ProgressBarProps
	extends Omit<AriaProgressBarProps, 'className'>,
		VariantProps<typeof progressBarVariants> {
	className?: string
}

/**
 * Accessible progress bar primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Proper ARIA attributes provided by React Aria's `<ProgressBar>`.
 * - **Styling:** All sizing and colors use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Sizes:** Small (h-1), medium (h-2, default), large (h-3).
 * - **Fill:** the brand primary color.
 * - **Indeterminate:** Supports indeterminate state for unknown progress.
 *
 * @example Basic usage
 * ```tsx
 * import { ProgressBar } from '@atlas/ui-primitives'
 *
 * function MyComponent() {
 *   const [progress, setProgress] = useState(0)
 *
 *   return (
 *     <ProgressBar
 *       value={progress}
 *       label="Upload progress"
 *     />
 *   )
 * }
 * ```
 *
 * @example Indeterminate state
 * ```tsx
 * <ProgressBar isIndeterminate label="Loading..." />
 * ```
 *
 * @see {@link progressBarVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/progress-bar/progress-bar.stories.tsx for visual examples
 */
export function ProgressBar({ size, className, ...props }: ProgressBarProps) {
	return (
		<AriaProgressBar {...props} className={cn('w-full', className)}>
			{({ percentage, isIndeterminate }) => (
				<div className={cn('w-full rounded-ds-full bg-ds-surface overflow-hidden', progressBarVariants({ size }))}>
					{/*
					 * Indeterminate renders a narrow fill swept across the track, not a
					 * full-width one fading in and out — movement is what distinguishes
					 * "still working" from "stalled". Determinate keeps the width
					 * transition so incremental progress animates rather than jumping.
					 */}
					<div
						className={cn(
							'h-full bg-ds-primary',
							isIndeterminate ? 'w-1/3 animate-ds-sweep' : 'transition-all duration-300',
						)}
						style={isIndeterminate ? undefined : { width: `${percentage}%` }}
					/>
				</div>
			)}
		</AriaProgressBar>
	)
}
